import {
  createCatalogEndpoint,
  createCrudEndpoints,
  createExportEndpoint,
} from '@/core/utils'
import {
  TCities,
  TCity,
  TComment,
  TFile,
  TFiles,
  TInvitation,
  TLead,
  TSeller,
  TStates,
  TStatuses,
} from '.'
import { DataWithPagination, TFilters } from '@/core/types'
import { toValue } from 'vue'
import api from '@/core/config/axios'

export type TInvitations = DataWithPagination<TInvitation>
export type TComments = TComment[]

export const serviceConfig = {
  model: 'invitations',
  version: 'v1',
  comments: {
    model: 'follow-ups',
    version: 'v1',
  },
  files: {
    model: 'files',
    version: 'v1',
  },
  sellers: {
    model: 'sellers',
    version: 'v1',
  },
  status: {
    model: 'status',
    version: 'v1',
  },
  leads: {
    model: 'leads',
    version: 'v1',
  },
  states: {
    model: 'states',
    version: 'v1',
  },
  cities: {
    model: 'cities',
    version: 'v1',
  },
}

const { comments, files, sellers, status, leads, states, cities } =
  serviceConfig

const commentsServiceHandlers = {
  ...createCrudEndpoints<TComments, TComment>(comments.model, comments.version),
  loadEstimationComments: async (params: TFilters): Promise<TComments> => {
    const urlSearchParams = [
      `page=${toValue(params.page) ?? 1}`,
      toValue(params.dynamicFilters ?? ''),
    ]
      .filter(Boolean)
      .join('&')

    return api
      .get(
        `${comments.version}/${comments.model}-estimation?${urlSearchParams}`,
      )
      .then((response) => response.data)
  },
  loadSellerComments: async (params: TFilters): Promise<TComments> => {
    const urlSearchParams = [
      `page=${toValue(params.page) ?? 1}`,
      toValue(params.dynamicFilters ?? ''),
    ]
      .filter(Boolean)
      .join('&')

    return api
      .get(`${comments.version}/${comments.model}-seller?${urlSearchParams}`)
      .then((response) => response.data)
  },
}

const moduleServiceHandlers = {
  ...createCrudEndpoints<TInvitations, TInvitation>(
    serviceConfig.model,
    serviceConfig.version,
  ),
  sendQuotationEmail: (payload: FormData): Promise<string> =>
    api.post(`v1/send-quotation`, payload).then((response) => response.data),
}

const filesServiceHandlers = createCrudEndpoints<TFiles, TFile>(
  files.model,
  files.version,
)

const leadsServiceHandlers = createCrudEndpoints<TLead[], TLead>(
  leads.model,
  leads.version,
)

const citiesServiceHandlers = createCrudEndpoints<TCities, TCity>(
  cities.model,
  cities.version,
)

const sellersCatalog = createCatalogEndpoint<TSeller[]>(
  sellers.model,
  sellers.version,
)

const statusCatalog = createCatalogEndpoint<TStatuses>(
  status.model,
  status.version,
)

const statesCatalog = createCatalogEndpoint<TStates>(
  states.model,
  states.version,
)

const citiesCatalog = createCatalogEndpoint<TCities>(
  cities.model,
  cities.version,
)

const leadsCatalog = createCatalogEndpoint<TLead[]>(leads.model, 'v2')

const moduleExportHandler = createExportEndpoint(serviceConfig.model)

const catalogServiceHandlers = {
  sellersCatalog,
  statusCatalog,
  leadsCatalog,
  statesCatalog,
  citiesCatalog,
}

export {
  moduleServiceHandlers,
  commentsServiceHandlers,
  citiesServiceHandlers,
  filesServiceHandlers,
  leadsServiceHandlers,
  catalogServiceHandlers,
  moduleExportHandler,
}
